import React, { useState, useRef, createElement } from 'react';
import { DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../../components/ui/Button';
import { Payment, Patient, WalkInClient } from '../../types';
import { useData } from '../../store/DataContext';
import { IssuerHeader } from './IssuerHeader';
import { calculateInvoiceTotals, formatCurrency } from './utils';
interface Props {
  payment: Payment;
  onClose: () => void;
}
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 17.78;
  const MARGIN_BOTTOM_MM = 17.78;
  const MARGIN_LEFT_MM = 12.7;
  const MARGIN_RIGHT_MM = 10.16;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
export const ReceiptModal: React.FC<Props> = ({
  payment,
  onClose
}) => {
  const {
    patients,
    walkInClients,
    invoices,
    users
  } = useData();
  const printableRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const patientCustomer: Patient | undefined = payment.customerType === 'Patient' ? patients.find(patient => patient.id === payment.customerId) : undefined;
  const walkInCustomer: WalkInClient | undefined = payment.customerType === 'WalkIn' ? walkInClients.find(client => client.id === payment.customerId) : undefined;
  const invoice = invoices.find(i => i.id === payment.invoiceId);
  const invoiceTotals = invoice ? calculateInvoiceTotals(invoice.items) : undefined;
  const cashier = users.find(u => u.id === payment.cashier);
  const handlePrint = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_${payment.receiptNumber}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-1668" data-mp="2z11jrquhbomh">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-1669" data-mp="11b9xz4vn2r92">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-1670" data-mp="14dwob4h30u25">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-1671" data-mp="199ln35la26xe">
            Receipt {payment.receiptNumber}
          </h3>
          <div className="flex items-center space-x-2" data-id="element-1672" data-mp="zd8l7kfkwmrk">
            <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-1673" data-mp="zbp4xttxabps">
              <PrinterIcon className="mr-1 h-4 w-4" data-id="element-1674" data-mp="ll2hwyxiya9j" /> Print
            </Button>
            <Button variant="primary" size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-1675" data-mp="qdc5i8b62y8n">
              <DownloadIcon className="mr-1 h-4 w-4" data-id="element-1676" data-mp="bm39nhgo8gn2" />
              {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded p-1.5 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close receipt" data-id="element-1677" data-mp="2oapo0008ktc">
              <XIcon className="h-5 w-5 text-gray-500" data-id="element-1678" data-mp="3k818hmjhw75k" />
            </button>
          </div>
        </div>

        <div ref={printableRef} className="billing-document bg-white p-0 text-black" data-id="element-1679" data-mp="3f96q2lpfwklz">
          <header className="mb-8 flex flex-col gap-6 border-b-2 border-black pb-6 sm:flex-row sm:items-start sm:justify-between" data-id="element-1680" data-mp="3p59q9m0qe96a">
            <IssuerHeader data-id="element-1681" data-mp="6ydamax16ssl" />
            <h1 className="text-left text-2xl font-bold uppercase tracking-[0.14em] text-gray-800 sm:text-right" data-id="element-1682" data-mp="6xkc1x8879lj">
              Official Receipt
            </h1>
          </header>

          <div className="grid gap-6 mb-8 sm:grid-cols-2" data-id="element-1683" data-mp="gnwunjwhcsp1">
            <div data-id="element-1684" data-mp="bqdbq6ou7rqr">
              <p className="mb-1" data-id="element-1685" data-mp="bpvcr2asizf5">
                <span className="font-semibold" data-id="element-1686" data-mp="bqwvzu1yms7y">Receipt #:</span>{' '}
                {payment.receiptNumber}
              </p>
              <p className="mb-1" data-id="element-1687" data-mp="kqth5lp558y9">
                <span className="font-semibold" data-id="element-1688" data-mp="krurtp28vtfg">Date:</span>{' '}
                {new Date(payment.date).toLocaleString()}
              </p>
              <p className="mb-1" data-id="element-1689" data-mp="3egoqqnnvyzz7">
                <span className="font-semibold" data-id="element-1690" data-mp="3jhmgblb86waq">Invoice #:</span>{' '}
                {invoice?.invoiceNumber || '—'}
              </p>
              <p className="mb-1" data-id="element-1691" data-mp="3nioa3mmdig5b">
                <span className="font-semibold" data-id="element-1692" data-mp="3nhmwbadz3fjw">Cashier:</span>{' '}
                {cashier?.fullName || '—'}
              </p>
            </div>
            <div className="text-right" data-id="element-1693" data-mp="14och3bbbjba">
              <p className="font-bold text-lg mb-1" data-id="element-1694" data-mp="15zh15cl0v3g">Received From:</p>
              {payment.customerType === 'Patient' ? <>
                  <p data-id="element-1695" data-mp="b1j7vdcah3lc">
                    {patientCustomer ? `${patientCustomer.firstName} ${patientCustomer.lastName}` : 'Registered patient'}
                  </p>
                  {patientCustomer && <p data-id="element-1696" data-mp="2cvili5cvvx17">File No: {patientCustomer.bmcFileNumber}</p>}
                </> : <p data-id="element-1697" data-mp="29bi53erlfs6n">
                  {invoice?.customerName || walkInCustomer?.name || 'Walk-in client'}
                </p>}
            </div>
          </div>

          <div className="border-t-2 border-b-2 border-gray-300 py-4 mb-8" data-id="element-1698" data-mp="1zl6zzow6bcy6">
            <div className="flex justify-between items-center mb-2" data-id="element-1699" data-mp="1zjcheh8djwx9">
              <span className="font-semibold text-lg" data-id="element-1700" data-mp="1vly7encvdl47">Payment Method:</span>
              <span className="text-lg" data-id="element-1701" data-mp="2s9b8ei64q44k">{payment.method}</span>
            </div>
            <div className="flex justify-between items-center" data-id="element-1702" data-mp="2narhe3coi25c">
              <span className="font-bold text-xl uppercase" data-id="element-1703" data-mp="2n9giohy3bkl3">Amount Paid:</span>
              <span className="font-bold text-2xl text-green-600" data-id="element-1704" data-mp="2j7uy0afbe20c">
                {formatCurrency(payment.amount)}
              </span>
            </div>
          </div>

          {invoice && invoiceTotals && <div className="bg-gray-50 p-4 rounded-lg mb-8" data-id="element-1705" data-mp="17sg3reehpo8x">
              <h4 className="font-bold uppercase text-gray-600 mb-2" data-id="element-1706" data-mp="12umvmftmmk60">
                Invoice Summary
              </h4>
              <div className="flex justify-between py-1" data-id="element-1707" data-mp="32we0ehbx8y4w">
                <span className="text-gray-600" data-id="element-1708" data-mp="36zj2wjw04ztj">Subtotal:</span>
                <span className="font-medium" data-id="element-1709" data-mp="36wy11uvpejvs">
                  {formatCurrency(invoiceTotals.subtotal)}
                </span>
              </div>
              {invoiceTotals.totalDiscount > 0 && <div className="flex justify-between py-1" data-id="element-1710" data-mp="3lo6vchajwtf9">
                  <span className="text-gray-600" data-id="element-1711" data-mp="3lop1c5n0mm0d">Discount:</span>
                  <span className="font-medium text-red-600" data-id="element-1712" data-mp="3qnjgmxlrxsfq">
                    -{formatCurrency(invoiceTotals.totalDiscount)}
                  </span>
                </div>}
              <div className="flex justify-between py-1" data-id="element-1713" data-mp="3gwx6vk0qnw4y">
                <span className="text-gray-600" data-id="element-1714" data-mp="3gvdptr79dh24">TOT Tax (5%):</span>
                <span className="font-medium" data-id="element-1715" data-mp="8gvcpb2gnbbn">
                  {formatCurrency(invoiceTotals.totalTax)}
                </span>
              </div>
              <div className="flex justify-between py-1 border-t border-gray-300 mt-1 pt-1" data-id="element-1716" data-mp="da6jxdb6pu43">
                <span className="font-bold text-gray-800" data-id="element-1717" data-mp="1xwfun9x8ipdu">
                  Total Invoice Amount:
                </span>
                <span className="font-bold text-gray-800" data-id="element-1718" data-mp="26qui3zrx5681">
                  {formatCurrency(invoiceTotals.totalAmount)}
                </span>
              </div>
              <div className="flex justify-between py-1" data-id="element-1719" data-mp="22rlpwzcj1gel">
                <span className="text-gray-600" data-id="element-1720" data-mp="1f0y2t6u1ik7g">Total Paid to Date:</span>
                <span className="font-medium text-green-600" data-id="element-1721" data-mp="1a13d36m040nz">
                  {formatCurrency(invoice.amountPaid)}
                </span>
              </div>
              <div className="flex justify-between py-1 border-t border-gray-300 mt-1 pt-1" data-id="element-1722" data-mp="1o1wabz3ot9us">
                <span className="font-bold text-gray-800" data-id="element-1723" data-mp="1j06vs9h9ae1y">
                  Remaining Balance:
                </span>
                <span className="font-bold text-red-600" data-id="element-1724" data-mp="2zhkxaqgxy8vl">
                  {formatCurrency(invoice.balanceDue)}
                </span>
              </div>
            </div>}

          {payment.notes && <div className="mb-8" data-id="element-1725" data-mp="37odhlnct5uka">
              <h4 className="font-bold uppercase text-gray-600 mb-1" data-id="element-1726" data-mp="37pes9qpwwf1h">Notes</h4>
              <p className="text-gray-700" data-id="element-1727" data-mp="32x52qbsplgzq">{payment.notes}</p>
            </div>}

          <div className="flex justify-between items-end mt-12 pt-8 border-t border-gray-200" data-id="element-1728" data-mp="2ywbe1g7fzy5s">
            <div className="text-center" data-id="element-1729" data-mp="2twp53iynd42s">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${payment.receiptNumber}`} alt="QR Code" className="w-24 h-24 mx-auto mb-2" data-id="element-1730" data-mp="2txrwdrzvt8j8" />
              <p className="text-xs text-gray-500" data-id="element-1731" data-mp="2k0vkdcary49x">Scan to verify</p>
            </div>
            <div className="text-center" data-id="element-1732" data-mp="2fz0io6oxlovm">
              <p className="text-sm italic text-gray-600 mb-4" data-id="element-1733" data-mp="i75z2eip5u2x">
                Thank you for your business!
              </p>
              <div className="border-t border-black w-48 mx-auto pt-1" data-id="element-1734" data-mp="e6vwlbu45lst">
                <p className="text-xs font-bold uppercase" data-id="element-1735" data-mp="1w3pjagplbma2">
                  Authorized Signature
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};